/**
 * Sunrise Ticket Page
 *
 * Read-only view of a legacy Sunrise CRM ticket. Replaces the `sunrise-crm:`
 * deep link, since hardly anyone still has the Sunrise client installed.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LinkIcon from '@mui/icons-material/Link';
import LoginIcon from '@mui/icons-material/Login';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { SearchBar } from '../components';
import { APP_BAR_SEARCH_SLOT_ID } from '../components/TopAppBar';
import { MarkdownContent } from '../components/MarkdownContent';
import { useAuth } from '../auth';
import { apiService, ApiServiceError } from '../services';
import { getSourceLabel, type DocumentResponse } from '../types/search';
import { getSourceFallbackIcon } from '../utils/sourceIcons';
import { splitSunriseContent, sunriseDocumentId } from '../utils/sunrise';
import noItemsLight from '../assets/light/no-items.png';
import noItemsDark from '../assets/dark/no-items.png';
import errorLight from '../assets/light/error-500.png';
import errorDark from '../assets/dark/error-500.png';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; doc: DocumentResponse }
  | { status: 'not-found' }
  | { status: 'error' };

const SUNRISE_URL_SCHEME = 'sunrise-crm:';
// Clears the fixed desktop app bar when the details panel sticks.
const STICKY_TOP = 104;

// -----------------------------------------------------------------------------
// Building blocks
// -----------------------------------------------------------------------------

interface TicketSectionProps {
  id: string;
  icon: ReactNode;
  title: string;
  markdown: string;
  emptyText: string;
  highlighted?: boolean;
}

function TicketSection({ id, icon, title, markdown, emptyText, highlighted }: TicketSectionProps) {
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{
        mb: 4,
        ...(highlighted && {
          bgcolor: 'surfaceContainerLow',
          borderLeft: 3,
          borderColor: 'primary.main',
          borderRadius: '0 6px 6px 0',
          p: { xs: 2, sm: 3 },
        }),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 1,
            bgcolor: 'primaryContainer',
            color: 'onPrimaryContainer',
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
        <Typography id={id} variant="h4" component="h2">
          {title}
        </Typography>
      </Stack>
      {markdown ? (
        <MarkdownContent>{markdown}</MarkdownContent>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyText}
        </Typography>
      )}
    </Box>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

interface TicketStatusProps {
  image: string;
  title: string;
  description: string;
  actions: ReactNode;
}

function TicketStatus({ image, title, description, actions }: TicketStatusProps) {
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: { xs: 4, sm: 8 },
      }}
    >
      <Box
        component="img"
        src={image}
        alt=""
        sx={{ width: '60%', maxWidth: 320, height: 'auto', mb: 4 }}
      />
      <Typography
        variant="h6"
        component="h1"
        sx={{
          color: '#F15A24',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 1,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480, mb: 3 }}>
        {description}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {actions}
      </Stack>
    </Box>
  );
}

function TicketSkeleton() {
  return (
    <Box
      aria-busy
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 300px' },
        columnGap: 5,
        rowGap: 3,
      }}
    >
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box>
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="text" width={180} height={14} />
          </Box>
        </Stack>
        <Skeleton variant="text" width="80%" height={44} sx={{ mb: 4 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 4 }} />
        <Skeleton variant="text" width={220} height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="95%" />
        <Skeleton variant="text" width="70%" />
      </Box>
      <Skeleton variant="rounded" height={280} sx={{ display: { xs: 'none', md: 'block' } }} />
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

export function SunriseTicketPage() {
  const { t, i18n } = useTranslation('search');
  const { t: tCommon } = useTranslation('common');
  const { ticketId } = useParams<{ ticketId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated, isLoading: authLoading, accessToken, login } = useAuth();

  const documentId = sunriseDocumentId(ticketId);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [query, setQuery] = useState('');
  const [searchSlot, setSearchSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSearchSlot(document.getElementById(APP_BAR_SEARCH_SLOT_ID));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!documentId) {
      setState({ status: 'not-found' });
      return;
    }

    let cancelled = false;
    // Keep showing the ticket while a renewed token triggers a refetch.
    setState((prev) =>
      prev.status === 'loaded' && prev.doc.id === documentId ? prev : { status: 'loading' }
    );
    apiService.setAccessToken(accessToken);
    apiService.getDocument(documentId).then(
      (doc) => {
        if (!cancelled) setState({ status: 'loaded', doc });
      },
      (err: unknown) => {
        if (cancelled) return;
        const notFound = err instanceof ApiServiceError && err.status === 404;
        setState({ status: notFound ? 'not-found' : 'error' });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [documentId, accessToken, authLoading, reloadKey]);

  const doc = state.status === 'loaded' ? state.doc : null;

  useEffect(() => {
    if (!doc?.title) return;
    const previous = document.title;
    document.title = `${doc.title} · ${tCommon('appName')}`;
    return () => {
      document.title = previous;
    };
  }, [doc?.title, tCommon]);

  const sections = useMemo(() => splitSunriseContent(doc?.content_full_md), [doc]);

  const rawNumber = doc?.source_fields?.ticket_number;
  const ticketNumber =
    typeof rawNumber === 'string' || typeof rawNumber === 'number' ? String(rawNumber) : '';

  const createdLabel = useMemo(() => {
    if (!doc?.created_at) return '';
    const date = new Date(doc.created_at);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(date);
  }, [doc?.created_at, i18n.language]);

  const sunriseUrl = doc?.url?.startsWith(SUNRISE_URL_SCHEME) ? doc.url : null;

  // `location.key` is "default" when the page was opened directly (no in-app history).
  const canGoBack = location.key !== 'default';
  const handleBack = useCallback(() => {
    if (canGoBack) navigate(-1);
    else navigate('/');
  }, [canGoBack, navigate]);

  const handleSearch = useCallback(
    (nextQuery: string) => {
      const trimmed = nextQuery.trim();
      if (trimmed) navigate(`/?q=${encodeURIComponent(trimmed)}`);
    },
    [navigate]
  );

  const copyToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: t('ticket.copied') });
      } catch {
        setSnackbar({ open: true, message: t('ticket.copyFailed') });
      }
    },
    [t]
  );

  const isDark = theme.palette.mode === 'dark';
  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={handleBack}
      sx={{
        mb: { xs: 2, sm: 3 },
        ml: -1,
        px: 1,
        py: 0.5,
        color: 'text.secondary',
        '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
      }}
    >
      {canGoBack ? t('ticket.backToResults') : t('ticket.backToSearch')}
    </Button>
  );

  let content: ReactNode;
  if (state.status === 'loading' || authLoading) {
    content = <TicketSkeleton />;
  } else if (state.status === 'not-found' && !isAuthenticated) {
    content = (
      <TicketStatus
        image={isDark ? noItemsDark : noItemsLight}
        title={t('ticket.signIn.title')}
        description={t('ticket.signIn.description')}
        actions={
          <Button variant="contained" startIcon={<LoginIcon />} onClick={login}>
            {t('ticket.signIn.action')}
          </Button>
        }
      />
    );
  } else if (state.status === 'not-found') {
    content = (
      <TicketStatus
        image={isDark ? noItemsDark : noItemsLight}
        title={t('ticket.notFound.title')}
        description={t('ticket.notFound.description')}
        actions={
          <Button variant="contained" onClick={() => navigate('/')}>
            {t('ticket.backToSearch')}
          </Button>
        }
      />
    );
  } else if (state.status === 'error') {
    content = (
      <TicketStatus
        image={isDark ? errorDark : errorLight}
        title={tCommon('errors.serverError.title')}
        description={tCommon('errors.serverError.description')}
        actions={
          <Button variant="contained" onClick={() => setReloadKey((k) => k + 1)}>
            {t('ticket.retry')}
          </Button>
        }
      />
    );
  } else if (doc) {
    content = (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 300px' },
          gridTemplateRows: { md: 'auto 1fr' },
          gridTemplateAreas: {
            xs: '"header" "body" "details"',
            md: '"header details" "body details"',
          },
          columnGap: 5,
          rowGap: { xs: 3, md: 4 },
          alignItems: 'start',
        }}
      >
        {/* Header */}
        <Box component="header" sx={{ gridArea: 'header', minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Avatar
              variant="circular"
              src={getSourceFallbackIcon(doc.source, doc.source_type)}
              alt=""
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, display: 'block' }}>
                {getSourceLabel(doc.source)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2, display: 'block' }}
              >
                {t(`facets.values.source_type.${doc.source_type}`, { defaultValue: doc.source_type })}
                {ticketNumber && ` · #${ticketNumber}`}
              </Typography>
            </Box>
            <Tooltip title={t('ticket.archivedTooltip')} arrow>
              <Chip
                size="small"
                variant="outlined"
                icon={<Inventory2OutlinedIcon fontSize="small" />}
                label={t('ticket.archived')}
                sx={{
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: 'text.secondary' },
                }}
              />
            </Tooltip>
          </Stack>
          <Typography variant="h2" component="h1" sx={{ overflowWrap: 'anywhere' }}>
            {doc.title || t('results.untitled')}
          </Typography>
        </Box>

        {/* Details */}
        <Box
          component="aside"
          aria-label={t('ticket.details')}
          sx={{
            gridArea: 'details',
            position: { md: 'sticky' },
            top: { md: STICKY_TOP },
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            p: 2.5,
          }}
        >
          <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
            {t('ticket.details')}
          </Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', rowGap: 1.5 }}>
            {ticketNumber && (
              <DetailRow label={t('ticket.fields.number')}>
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  #{ticketNumber}
                </Box>
                <Tooltip title={t('ticket.copyNumber')}>
                  <IconButton
                    size="small"
                    aria-label={t('ticket.copyNumber')}
                    onClick={() => copyToClipboard(ticketNumber)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </DetailRow>
            )}
            {createdLabel && (
              <DetailRow label={t('ticket.fields.created')}>
                <Box component="time" dateTime={doc.created_at}>
                  {createdLabel}
                </Box>
              </DetailRow>
            )}
            {doc.language && (
              <DetailRow label={t('ticket.fields.language')}>
                {t(`languages.${doc.language}`, { defaultValue: doc.language.toUpperCase() })}
              </DetailRow>
            )}
            <DetailRow label={t('ticket.fields.source')}>{getSourceLabel(doc.source)}</DetailRow>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={() => copyToClipboard(window.location.href)}
              fullWidth
            >
              {t('ticket.copyLink')}
            </Button>
            {sunriseUrl && (
              <>
                <Button variant="text" startIcon={<OpenInNewIcon />} href={sunriseUrl} fullWidth>
                  {t('ticket.openInSunrise')}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {t('ticket.openInSunriseHint')}
                </Typography>
              </>
            )}
          </Stack>
        </Box>

        {/* Body */}
        <Box component="article" sx={{ gridArea: 'body', minWidth: 0 }}>
          {doc.summary && (
            <Box
              component="section"
              aria-labelledby="ticket-summary"
              sx={{ bgcolor: 'surfaceContainer', borderRadius: 1.5, p: { xs: 2, sm: 2.5 }, mb: 4 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesomeOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography id="ticket-summary" variant="subtitle1" component="h2">
                  {t('ticket.summary.title')}
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {doc.summary}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                {t('ticket.summary.hint')}
              </Typography>
            </Box>
          )}

          <TicketSection
            id="ticket-description"
            icon={<DescriptionOutlinedIcon />}
            title={t('ticket.description')}
            markdown={sections.description}
            emptyText={t('ticket.noDescription')}
          />
          <TicketSection
            id="ticket-solution"
            icon={<TaskAltIcon />}
            title={t('ticket.solution')}
            markdown={sections.solution}
            emptyText={t('ticket.noSolution')}
            highlighted
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, sm: 4 }, pl: { xs: 2, sm: 3, lg: '224px' }, pr: { xs: 2, sm: 3 } }}>
      {searchSlot &&
        createPortal(
          <SearchBar query={query} onQueryChange={setQuery} onSearch={handleSearch} />,
          searchSlot
        )}

      <Box sx={{ maxWidth: 1120 }}>
        {backButton}
        {content}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
